<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { PersonMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import RuneTowerRank from './RuneTowerRank.vue'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

defineProps<{ monster?: PersonMonsterType }>()
const emits = defineEmits(['start', 'sweep'])

function handleStartRuneTower(isAuto = false) {
  if (userStore.runeTowerTimes <= 0) return ElMessage.warning('本层挑战次数已耗尽')
  const curTime = dayjs()
  const week = curTime.day()
  if (week === 6) {
    const hour = curTime.hour()
    if (hour === 23) {
      const min = curTime.minute()
      if (min >= 55) return ElMessage.warning('符石尖塔结算中，无法进入挑战')
    }
  }
  if (userStore.runeList.length > userStore.getRuneCapacity) return ElMessage.warning('符石背包已满，无法进行战斗')

  ElMessageBox.confirm(
    `<p class="affix">确定进入【符石尖塔】挑战吗？（不论成败都将<span class="shrink">消耗</span>挑战次数）${
      userStore.runeTowerTimes < 3 ? '<br /><b>【若消耗所有挑战次数，则本周无法再进行符石尖塔挑战】</b>' : ''
    }${isAuto ? '<br />您选择了【自动】挑战，将自动挑战至失败为止' : ''}</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startRuneTower', data: { isAuto } })
      userStore.runeTowerTimes--
      emits('start')
    })
    .catch(() => {})
}
function handleSweepRuneTower() {
  if (userStore.runeTowerLevel >= 10) return ElMessage.warning('您已通关符石尖塔')
  ElMessageBox.confirm('<p class="affix">确定<span class="info">扫荡</span>符石尖塔吗？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'sweepRuneTower' })
      userStore.runeTowerTimes = 10
      userStore.runeTowerLevel = 10
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
      emits('sweep')
    })
    .catch(() => {})
}
const rankShow = ref(false)
</script>

<template>
  <div class="border-wrap rune-tower">
    <h4 class="awaken">
      符石尖塔
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>符石尖塔说明</h4>
            <p>1. 符石尖塔<span class="info">每周日0点刷新</span>，每次刷新塔内都将被随机的怪物占领，同时怪物的各项数值也是随机的。</p>
            <p>2. 每层仅有<span class="info">10次</span>挑战机会，进入则扣除次数（无论成败），若无次数则本周无法再挑战。</p>
            <p>3. 您需要在<span class="info">3分钟</span>的时间内通过挑战，进入后切装无效，请提前根据怪物属性搭配合理的装备。</p>
            <p>4. 奖励规则：</p>
            <p style="text-indent: 2em">
              a.
              <span class="gold">金币</span>奖励：1~10层每层奖励200,000金币。
            </p>
            <p style="text-indent: 2em">
              b. <span class="antiquity">特殊</span>奖励：1 层、3层、5层，将固定奖励 2 <span class="awaken">符石碎星</span>。
            </p>
            <p style="text-indent: 2em">
              c.
              <span class="awaken">符石</span>奖励：每层均会产出 1个随机符石。1层，必定为<span class="epic">史诗</span>符石；3层，必定为<span
                class="legend"
                >传说</span
              >符石。
            </p>
            <p style="text-indent: 2em">
              d. <span class="info">随机</span>奖励：每层都有概率触发一次随机奖励：<span class="info">50%</span>概率 1 强化保护券；<span class="info"
                >10%</span
              >概率 1 <span class="awaken">符石碎星</span>。
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>
      <span style="margin-right: 15px">当前层级：{{ userStore.runeTowerLevel + 1 }}</span>
      <span>剩余次数：{{ userStore.runeTowerTimes }}</span>
    </p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!monster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="monster">{{ monster?.name }}</span>
            <span v-show="!monster">暂无战斗</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ monster?.name }}</h3>
          <p>血量：{{ monster?.hp }}</p>
          <p>防御：{{ monster?.def }}</p>
          <p>闪避率：{{ monster?.evs }}%</p>
          <p>抗爆率：{{ monster?.crtdef }}%</p>
          <template v-if="monster?.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(monster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartRuneTower(false)" :disabled="!userStore.soulType">
        {{ !userStore.soulType ? '觉醒后解锁' : '进入' }}
      </el-button>
      <el-button
        color="#ffb3a7"
        :dark="commonStore.isDarkMode"
        plain
        @click="handleStartRuneTower(true)"
        v-show="userStore.soulType && userStore.soulType <= 3"
        >自动</el-button
      >
      <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweepRuneTower" v-show="userStore.soulType > 3">扫荡</el-button>
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <RuneTowerRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.rune-tower {
  flex-flow: column;
}
</style>
